/**
 * O seletor de Profissional do modal de responsável (#638).
 *
 * Lista só os profissionais que podem ocupar o papel escolhido (tabela em
 * `PROFISSIONAIS_POR_TIPO_DE_RESPONSAVEL`) e diz, em cada situação, por que o campo está como
 * está: carregando, sem acesso, falha, sem ninguém do tipo.
 *
 * É um componente à parte, montado com `key` pelo tipo, por dois motivos: a lista de um tipo
 * nunca vaza para o seguinte (o gancho recomeça do zero), e o gancho que pergunta a permissão
 * (`professional.read`) mora no arquivo que faz a chamada — a guarda de acesso da suíte só
 * herda de pai para filho.
 *
 * "Desabilitado" aqui é só a propriedade `disabled` do `Select`. O `disabled` do react-hook-form
 * faria o valor sumir no envio, e quem edita sem `professional.read` perderia o vínculo gravado.
 */
import React, { useCallback, useEffect, useId, useState } from 'react';
import { Controller, type Control } from 'react-hook-form';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material';

import { useListaPermitida } from '../../../../authorization/useListaPermitida';
import PrerequisiteBlock from '../../../../components/common/PrerequisiteBlock';
import { professionalService } from '../../../../services/professionalService';
import { ApiRequestError } from '../../../../types/common';
import {
  PROFISSIONAIS_POR_TIPO_DE_RESPONSAVEL,
  type CompanyResponsibleFormData,
  type ResponsibleType,
} from '../../../../types/company';
import { PROFESSIONAL_TYPE_LABELS } from '../../../../types/professional';

const FRASE_DE_FALHA = 'Não foi possível carregar os profissionais agora';
const ROTA_DE_CADASTRO = '/professionals/create';
const ROTULO_DO_VINCULO_ATUAL = '(vínculo atual, fora da lista)';

interface Profissional {
  readonly id: number | string;
  readonly name: string;
}

interface OpcaoDoMenu {
  readonly id: string;
  readonly name: string;
  readonly foraDaLista: boolean;
}

export interface ResponsibleProfessionalFieldProps {
  control: Control<CompanyResponsibleFormData>;
  tipo: ResponsibleType;
  /** O modal está aberto. Fechado, nada é pedido e nenhum estado vazio é desenhado. */
  habilitado: boolean;
  /** O profissional já gravado no responsável que se edita, com o nome que veio dele. */
  vinculoAtual?: { id: number | string; name: string } | null;
}

const OCULTO_PARA_OS_OLHOS = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
} as const;

function nomesDosTipos(tipos: readonly string[]): string {
  return tipos
    .map((tipo) => PROFESSIONAL_TYPE_LABELS[tipo as keyof typeof PROFESSIONAL_TYPE_LABELS] ?? tipo)
    .join(', ');
}

/** "do tipo X" quando há um só; "de um destes tipos: X, Y" quando há vários. */
function descreverTipos(tipos: readonly string[]): string {
  return tipos.length === 1
    ? `do tipo ${nomesDosTipos(tipos)}`
    : `de um destes tipos: ${nomesDosTipos(tipos)}`;
}

const ResponsibleProfessionalField: React.FC<ResponsibleProfessionalFieldProps> = ({
  control,
  tipo,
  habilitado,
  vinculoAtual = null,
}) => {
  const tiposPedidos = PROFISSIONAIS_POR_TIPO_DE_RESPONSAVEL[tipo] ?? null;
  const idDaAjuda = `profissional-ajuda-${useId()}`;

  // Estável por tipo: um `carregar` novo a cada render faria o gancho pedir sem parar.
  const carregar = useCallback(async (): Promise<readonly Profissional[]> => {
    const filtro = PROFISSIONAIS_POR_TIPO_DE_RESPONSAVEL[tipo] ?? undefined;
    try {
      return await professionalService.getProfessionalsForDropdown(filtro);
    } catch (falha) {
      // 400 é o servidor recusando o tipo pedido: a mensagem dele diz qual. O resto (rede, 5xx)
      // traz texto técnico que não ajuda quem usa.
      if (falha instanceof ApiRequestError && falha.status === 400) throw new Error(falha.message);
      throw new Error(FRASE_DE_FALHA);
    }
  }, [tipo]);

  const lista = useListaPermitida<Profissional>('professional.read', 'Profissionais', carregar, {
    habilitado,
  });
  const { recarregar } = lista;

  // A última resposta foi uma lista? Se foi, uma nova busca é "atualizar" e não "carregar":
  // o campo continua utilizável, para quem já escolheu alguém não perder a escolha na tela.
  const [ultimaRespostaFoiLista, setUltimaRespostaFoiLista] = useState(false);
  const respostaDestaVezFoiLista = lista.resolvida && lista.permitido && !lista.erro;
  if (lista.resolvida && ultimaRespostaFoiLista !== respostaDestaVezFoiLista) {
    setUltimaRespostaFoiLista(respostaDestaVezFoiLista);
  }

  // Só depois do clique em "Cadastrar profissional" (que abre outra aba) vale ouvir a volta.
  const [aguardandoVolta, setAguardandoVolta] = useState(false);
  useEffect(() => {
    if (!aguardandoVolta || !habilitado) return undefined;
    window.addEventListener('focus', recarregar);
    return () => window.removeEventListener('focus', recarregar);
  }, [aguardandoVolta, habilitado, recarregar]);

  // O botão do bloco é um link para outra aba e não avisa quando é clicado: o clique é lido aqui.
  const aoClicarNoBloco = (evento: React.MouseEvent<HTMLElement>) => {
    const alvo = evento.target as HTMLElement;
    if (alvo.closest(`a[href="${ROTA_DE_CADASTRO}"]`)) setAguardandoVolta(true);
  };

  const [escolhidoAgora, setEscolhidoAgora] = useState<OpcaoDoMenu | null>(null);

  const atualizando = habilitado && !lista.resolvida && ultimaRespostaFoiLista;
  const carregandoPelaPrimeiraVez = habilitado && !lista.resolvida && !atualizando;
  const semAcesso = habilitado && lista.resolvida && !lista.permitido;
  const comFalha = habilitado && lista.resolvida && lista.permitido && Boolean(lista.erro);
  const respostaEmMaos = habilitado && (lista.resolvida || atualizando);
  const vazio = respostaEmMaos && lista.permitido && !lista.erro && lista.itens.length === 0;
  const comLista = respostaEmMaos && lista.permitido && !lista.erro;

  const montarOpcoes = (valorAtual: string): OpcaoDoMenu[] => {
    const daLista: OpcaoDoMenu[] = lista.itens.map((item) => ({
      id: String(item.id),
      name: item.name,
      foraDaLista: false,
    }));
    const idsDaLista = new Set(daLista.map((opcao) => opcao.id));
    const opcoes = [...daLista];

    // O gravado fica no menu mesmo fora da lista (e mesmo depois de trocar de tipo), para a
    // pessoa poder voltar a ele. Ids chegam como texto: compara-se sempre por String(...).
    if (vinculoAtual && !idsDaLista.has(String(vinculoAtual.id))) {
      opcoes.unshift({ id: String(vinculoAtual.id), name: vinculoAtual.name, foraDaLista: true });
    }

    // O valor escolhido agora nunca sai do menu, nem se uma recarga o tirar da lista.
    const escolhido = escolhidoAgora;
    if (valorAtual && escolhido && escolhido.id === valorAtual && !opcoes.some((o) => o.id === valorAtual)) {
      opcoes.push(escolhido);
    }
    return opcoes;
  };

  const textoDoCampo = (): React.ReactNode => {
    if (carregandoPelaPrimeiraVez) {
      return (
        <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CircularProgress size={16} aria-hidden />
          Carregando profissionais…
        </Box>
      );
    }
    if (semAcesso) return 'Profissionais indisponíveis';
    if (comFalha) return 'Não foi possível carregar';
    if (vazio) return 'Nenhum profissional disponível';
    return 'Selecione...';
  };

  const anuncio = (() => {
    if (carregandoPelaPrimeiraVez) return 'Carregando profissionais…';
    if (atualizando) return 'Atualizando…';
    if (comLista) {
      const total = lista.itens.length;
      return total === 1 ? '1 profissional disponível' : `${total} profissionais disponíveis`;
    }
    return '';
  })();

  const linhaDeApoio = (() => {
    if (!habilitado || !(carregandoPelaPrimeiraVez || comLista)) return null;
    if (tiposPedidos) return `Mostrando só: ${nomesDosTipos(tiposPedidos)}`;
    return 'Qualquer profissional que você enxerga pode ser o responsável legal';
  })();

  const tiposDescritos = tiposPedidos ? descreverTipos(tiposPedidos) : null;

  const temAjuda = Boolean(linhaDeApoio) || semAcesso || comFalha;

  return (
    <>
    <Controller
      name="professionalId"
      control={control}
      render={({ field, fieldState }) => {
        const valor = field.value ?? '';
        const opcoes = montarOpcoes(valor);
        const desabilitado =
          !habilitado ||
          carregandoPelaPrimeiraVez ||
          semAcesso ||
          comFalha ||
          (vazio && opcoes.length === 0);

        return (
          <FormControl fullWidth error={Boolean(fieldState.error)}>
            <InputLabel shrink>Profissional</InputLabel>
            <Select
              {...field}
              value={valor}
              label="Profissional"
              notched
              displayEmpty
              disabled={desabilitado}
              inputProps={{ 'aria-describedby': temAjuda ? idDaAjuda : undefined }}
              renderValue={(selecionado) => {
                const atual = opcoes.find((opcao) => opcao.id === String(selecionado));
                return selecionado && atual ? atual.name : textoDoCampo();
              }}
              onChange={(evento) => {
                setEscolhidoAgora(
                  opcoes.find((opcao) => opcao.id === String(evento.target.value)) ?? null
                );
                field.onChange(evento);
              }}
            >
              <MenuItem value="">Selecione...</MenuItem>
              {opcoes.map((opcao) => (
                <MenuItem key={opcao.id} value={opcao.id}>
                  {opcao.foraDaLista ? `${opcao.name} ${ROTULO_DO_VINCULO_ATUAL}` : opcao.name}
                </MenuItem>
              ))}
            </Select>

            {fieldState.error && <FormHelperText>{fieldState.error.message}</FormHelperText>}

            {linhaDeApoio && (
              <FormHelperText id={idDaAjuda} error={false}>
                {linhaDeApoio}
              </FormHelperText>
            )}
            {semAcesso && (
              <FormHelperText id={idDaAjuda} error={false}>
                {vinculoAtual
                  ? 'Seu perfil não inclui ver profissionais, então a lista não abre. O vínculo atual continua e será mantido ao salvar.'
                  : 'Seu perfil não inclui ver profissionais. Peça a um administrador.'}
              </FormHelperText>
            )}
            {comFalha && (
              <>
                <FormHelperText id={idDaAjuda} error>
                  {lista.erro}
                </FormHelperText>
                <Box sx={{ mt: 0.5, mx: 1 }}>
                  <Button size="small" onClick={recarregar}>
                    Tentar de novo
                  </Button>
                </Box>
              </>
            )}

            <Box role="status" sx={OCULTO_PARA_OS_OLHOS}>
              {anuncio}
            </Box>
          </FormControl>
        );
      }}
    />
    {vazio && (
      <Box sx={{ mt: 1 }} onClick={aoClicarNoBloco}>
        <PrerequisiteBlock
          title={
            tiposPedidos
              ? 'Nenhum profissional deste tipo disponível para você'
              : 'Nenhum profissional disponível para você'
          }
          reason={
            tiposDescritos
              ? `O responsável precisa ser um profissional ${tiposDescritos}. Cadastros antigos sem tipo preenchido também não aparecem aqui: se o profissional já existe, complete o tipo dele em vez de cadastrar de novo.`
              : 'Ainda não há nenhum profissional que você consiga ver. Cadastre um para poder escolhê-lo como responsável legal.'
          }
          resolve={{
            label: 'Cadastrar profissional',
            permission: 'professional.create',
            mode: 'navigate',
            route: ROTA_DE_CADASTRO,
          }}
          actions={[
            { route: '/professionals', label: 'Ver lista de profissionais', requiredPermission: 'professional.read' },
          ]}
          deniedHint={
            tiposDescritos
              ? `Peça a um administrador para cadastrar um profissional ${tiposDescritos}, ou para completar o tipo de um cadastro antigo.`
              : 'Peça a um administrador para cadastrar um profissional.'
          }
          openInNewTab
        />
      </Box>
    )}
    </>
  );
};

export default ResponsibleProfessionalField;
